// src/main.js
import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import { isPostAuthRoutePath } from './newui/router/postAuthRouteCatalog'
import { initSentry } from './observability/sentry'
import { applyPreBootstrapAuthGate } from './auth/preBootstrapAuthGate'

// Initialize Sentry FIRST so unhandled errors from either runtime
// (Vue legacy or React newui) are captured. No-op when VITE_SENTRY_DSN
// is unset (i.e. local dev).
initSentry()

// Styles
import './index.css'
import './styles/shapes.css'
import '@fortawesome/fontawesome-free/css/all.min.css'

import { initInactivityTimer, logoutIfExpired, isAuthenticated } from "./utils/auth";
import { initTheme } from './utils/theme';

// When VITE_AUTH_PROVIDER=workos and the user lands on a legacy auth
// page (/login, /register, /forgot-password, /reset-password), inject a
// branded splash and redirect to AuthKit immediately — BEFORE mounting
// Vue or React. This eliminates the legacy-form flash that previously
// appeared for a frame while the page's onMounted hook navigated away.
// Respects the `?provider=keycloak` override. When the gate engages we
// short-circuit bootstrap so neither runtime clobbers the splash.
const authGateEngaged = applyPreBootstrapAuthGate()
if (!authGateEngaged) {

logoutIfExpired(); // initial check
if (isAuthenticated()) {
  initInactivityTimer(7, 30); // 7-minute timeout with 30-second warning
}

initTheme();

const bootstrapPath = window.location.pathname
const isPublicViewPath = bootstrapPath.startsWith('/view/gates/') || bootstrapPath === '/nda/sign'
const isAuthCallbackPath = bootstrapPath === '/callback'
const isLoggedOutPath = bootstrapPath === '/logged-out'

if (isPostAuthRoutePath(bootstrapPath) || isPublicViewPath || isAuthCallbackPath || isLoggedOutPath) {
  // Lazy-load React runtime only for post-auth routes
  Promise.all([
    import('react'),
    import('react-dom/client'),
    import('./newui/embedded/PostAuthEntry'),
    import('./observability/RootErrorBoundary'),
  ]).then(([React, { createRoot }, { default: PostAuthEntry }, { RootErrorBoundary }]) => {
    createRoot(document.getElementById('app')).render(
      React.createElement(
        React.StrictMode,
        null,
        React.createElement(RootErrorBoundary, null, React.createElement(PostAuthEntry)),
      ),
    )
  })
} else {
  const app = createApp(App)
  app.use(router)

  // Forward Vue render/lifecycle errors to Sentry. window.onerror only
  // catches uncaught exceptions; Vue swallows component errors by default
  // unless we route them here.
  import('./observability/sentry').then(({ Sentry }) => {
    app.config.errorHandler = (err, _instance, info) => {
      Sentry.captureException(err, { extra: { vueInfo: info } })
      // Preserve dev-time console visibility.
      // eslint-disable-next-line no-console
      console.error('[vue:error]', err, info)
    }
  })

  // Lazy-load PrimeVue only when needed (Vue routes)
  import('primevue/config').then(({ default: PrimeVue }) => {
    app.use(PrimeVue)
    app.mount('#app')
  })
}

} // end if (!authGateEngaged)
